import React, { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { CircleHelp, X } from 'lucide-react';
import { Pill } from '../../components/ui';
import {
  COMMITMENT_GUIDE,
  RULE_GROUPS,
  SEVERITY_DEFINITIONS,
  type RuleSeverity,
} from '../../titleAnalysis/ruleGlossary';
import { severityTone } from './reportModel';

/**
 * RulesHelp: a "?" button for the Title analysis header that opens a side panel
 * explaining every rule the report is checked against, so a reader can
 * understand each exception. The panel opens from the right over the page,
 * closes with the X, the Escape key or a click outside, and returns focus to
 * the button. Light theme only.
 */
const SEVERITY_LABEL: Record<RuleSeverity, string> = {
  material: 'Material',
  curable: 'Curable',
  informational: 'Informational',
};

export interface RulesHelpProps {
  /** `onInk` styles the button for the dark Title analysis header. */
  variant?: 'onInk' | 'light';
}

const BUTTON_BASE =
  'inline-flex h-9 w-9 items-center justify-center rounded-[9px] focus:outline-none focus-visible:ring-2';
const BUTTON_VARIANT: Record<NonNullable<RulesHelpProps['variant']>, string> = {
  onInk:
    'border border-white/25 bg-white/10 text-white hover:bg-white/20 focus-visible:ring-white',
  light:
    'border border-gray-200 bg-white text-gray-700 hover:bg-gray-50 focus-visible:ring-[#834DFB]',
};

export const RulesHelp: React.FC<RulesHelpProps> = ({ variant = 'light' }) => {
  const [open, setOpen] = useState(false);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const closeRef = useRef<HTMLButtonElement>(null);

  const close = useCallback(() => {
    setOpen(false);
    buttonRef.current?.focus();
  }, []);

  useEffect(() => {
    if (!open) return undefined;
    closeRef.current?.focus();
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') close();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open, close]);

  return (
    <>
      <button
        ref={buttonRef}
        type="button"
        data-testid="rules-help-button"
        aria-label="What the rules mean"
        aria-haspopup="dialog"
        aria-expanded={open}
        title="What the rules mean"
        onClick={() => setOpen(true)}
        className={`${BUTTON_BASE} ${BUTTON_VARIANT[variant]}`}
      >
        <CircleHelp className="h-[17px] w-[17px]" strokeWidth={2} aria-hidden="true" />
      </button>

      {open
        ? createPortal(
            <div className="fixed inset-0 z-50 flex justify-end">
              <div
                data-testid="rules-help-backdrop"
                className="absolute inset-0 bg-black/30"
                onClick={close}
                aria-hidden="true"
              />
              <aside
                role="dialog"
                aria-modal="true"
                aria-labelledby="rules-help-title"
                data-testid="rules-help-panel"
                className="relative flex h-full w-[480px] max-w-full flex-col bg-white shadow-xl"
              >
                <header className="flex shrink-0 items-start justify-between gap-3 border-b border-gray-200 px-5 py-4">
                  <div>
                    <h2 id="rules-help-title" className="text-base font-bold text-gray-900">
                      Title analysis rules
                    </h2>
                    <p className="mt-1 text-[13px] text-gray-600">
                      Each exception in the report carries one rule code. The rule fixes its
                      severity, so the same defect always gets the same severity.
                    </p>
                  </div>
                  <button
                    ref={closeRef}
                    type="button"
                    data-testid="rules-help-close"
                    aria-label="Close"
                    onClick={close}
                    className="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-lg text-gray-600 hover:bg-[#F5F3FF] hover:text-[#5A21C9] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#834DFB]"
                  >
                    <X className="h-4 w-4" strokeWidth={2} aria-hidden="true" />
                  </button>
                </header>

                <div className="min-h-0 flex-1 overflow-y-auto px-5 py-4">
                  <section aria-label="Severity" data-testid="rules-help-severity">
                    <h3 className="text-[11px] font-bold uppercase tracking-[0.1em] text-gray-500">
                      Severity
                    </h3>
                    <dl className="mt-2 space-y-2">
                      {SEVERITY_DEFINITIONS.map((d) => (
                        <div key={d.severity} className="flex items-start gap-3">
                          <dt className="w-[104px] shrink-0">
                            <Pill tone={severityTone(d.severity)}>{d.label}</Pill>
                          </dt>
                          <dd className="text-[13px] text-gray-700">{d.meaning}</dd>
                        </div>
                      ))}
                    </dl>
                  </section>

                  {RULE_GROUPS.map((group) => (
                    <section
                      key={group.id}
                      aria-label={group.label}
                      data-testid={`rules-help-group-${group.id}`}
                      className="mt-6"
                    >
                      <h3 className="text-sm font-bold text-gray-900">{group.label}</h3>
                      <p className="mt-0.5 text-[13px] text-gray-600">{group.summary}</p>
                      <ul className="mt-2 m-0 list-none divide-y divide-gray-100 p-0">
                        {group.rules.map((rule) => (
                          <li
                            key={rule.code}
                            data-testid="rules-help-rule"
                            data-rule-code={rule.code}
                            className="py-3"
                          >
                            <div className="flex flex-wrap items-center gap-2">
                              <span className="rounded-[5px] bg-[#F5F3FF] px-1.5 py-px text-xs font-bold text-[#5A21C9]">
                                {rule.code}
                              </span>
                              <span className="text-sm font-bold text-gray-900">{rule.title}</span>
                              <Pill tone={severityTone(rule.severity)} className="ml-auto">
                                {SEVERITY_LABEL[rule.severity]}
                              </Pill>
                            </div>
                            <p className="mt-1 text-[13px] text-gray-700">{rule.meaning}</p>
                            {rule.note ? (
                              <p className="mt-1 text-xs text-gray-500">{rule.note}</p>
                            ) : null}
                          </li>
                        ))}
                      </ul>
                    </section>
                  ))}

                  <section
                    aria-label={COMMITMENT_GUIDE.label}
                    data-testid="rules-help-commitment"
                    className="mt-6"
                  >
                    <h3 className="text-sm font-bold text-gray-900">{COMMITMENT_GUIDE.label}</h3>
                    <p className="mt-0.5 text-[13px] text-gray-600">{COMMITMENT_GUIDE.summary}</p>
                    <ul className="mt-2 list-disc space-y-1 pl-5 text-[13px] text-gray-700">
                      {COMMITMENT_GUIDE.points.map((p) => (
                        <li key={p}>{p}</li>
                      ))}
                    </ul>
                  </section>
                </div>
              </aside>
            </div>,
            document.body
          )
        : null}
    </>
  );
};

export default RulesHelp;
